import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function BuryCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Bury Pothole Claims | 54,680 Repairs in Five Years | Fixtyer"
        description="Bury Council filled an estimated 54,680 potholes in five years, yet one in five unclassified roads remains in RED condition and B/C roads in RED nearly doubled since 2020. See what the DfT AMBER rating means for your claim."
        canonical="/councils/bury"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Bury', url: '/councils/bury' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Bury spends more than double its DfT allocation — can I still claim?',
            answer: 'Yes. The DfT Spend scorecard is GREEN, but your claim turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired under Section 58 — not on aggregate spending. Bury\'s overall rating is AMBER because road condition is AMBER: B and C roads in RED nearly doubled from 2.6% in 2022 to 7.2% in 2024, and 20.6% of unclassified roads remain in RED condition.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'Unclassified roads make up 499.8km — 79% of Bury\'s 629.2km network. The council surveys only 50% of U-roads each year, achieving full network coverage over two years. At the 2024 survey, 20.6% of U-roads were in RED condition — roughly 103km of residential streets and estate roads. If your incident fell in a year when your road was not surveyed, the council\'s own condition data may not cover it.',
          },
          {
            question: 'U-road RED condition peaked at 35.2% in 2022 — does the drop to 20.6% weaken my claim?',
            answer: 'No — one in five residential roads is still in RED condition at the 2024 survey. A drop from the 35.2% peak in 2022 does not mean your specific defect was reasonably known about or repaired — especially on a U-road that may only be condition-surveyed every other year under Bury\'s 50%-per-annum CVI cycle.',
          },
          {
            question: 'Does Bury\'s risk-based inspection approach strengthen its Section 58 defence?',
            answer: 'It gives the council something to point to — reactive repairs are identified through "routine highway safety inspections" that "adopt a risk-based approach, prioritising repairs to those defects which pose the greatest risk to road users." But risk-based prioritisation means lower-priority roads and defects can wait. If your pothole was not treated as high-risk, that is an argument about whether the inspection regime was reasonable for your road — not an automatic win for either side.',
          },
          {
            question: 'Bury fills around 11,000 potholes a year — does that mean the roads are fixed?',
            answer: 'The council filled an estimated 10,201 potholes in 2024/25 and plans to fill approximately 10,000 in 2025/26 — roughly 28 every day. Over five years the published estimates total 54,680 repairs on a network where condition data shows deterioration on B/C roads and one in five U-roads still in RED. Reactive filling is evidence of ongoing failure, not proof your defect was caught.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Bury Council"
        councilSlug="bury"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/bury"
        headline="Bury Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Bury</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Bury: One in Five Residential Roads Still in RED
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Bury Council spends more than double its DfT capital allocation and earns a GREEN spend
            scorecard — yet the overall rating is <strong className="text-amber-400">AMBER</strong> because
            road condition is AMBER. Unclassified roads — <strong className="text-rose-400">79% of the
            629km network</strong> — hit 35.2% RED in 2022 and still sit at 20.6%, while B and C roads
            in RED nearly doubled in two years.
          </p>

          {/* Key stat callout */}
          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">20.6%</div>
            <div className="text-white font-medium mt-2">Unclassified roads in RED condition (2024)</div>
            <div className="text-brand-300 text-sm mt-1">
              Roughly 103km of Bury's residential and estate roads — on a network where the council
              surveys only half of U-roads each year.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of condition data from Bury's own transparency report — classified roads
            slipping while spend stays GREEN
          </p>

          {/* Network overview */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">The network (629.2km total roads)</h3>
            <div className="grid sm:grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-brand-700">62.1km</div>
                <div className="text-sm text-surface-600">A roads (9.9%)</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-brand-700">67.3km</div>
                <div className="text-sm text-surface-600">B and C roads (10.7%)</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">499.8km</div>
                <div className="text-sm text-rose-800">Unclassified (79.4%)</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              Nearly four-fifths of Bury's carriageway network is unclassified — the roads where most
              pothole damage claims originate.
            </p>
          </div>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (62.1km): RED rising</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">3.7%</td>
                    <td className="px-4 py-2 text-center">24.3%</td>
                    <td className="px-4 py-2 text-center">72%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">4%</td>
                    <td className="px-4 py-2 text-center">20.6%</td>
                    <td className="px-4 py-2 text-center">75.4%</td>
                  </tr>
                  <tr className="bg-rose-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">6.3%</td>
                    <td className="px-4 py-2 text-center font-bold">20.7%</td>
                    <td className="px-4 py-2 text-center font-bold">73%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              A-road RED condition has risen 70% since 2020 (3.7% → 6.3%). Main routes are not immune.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (67.3km): sharp decline since 2022</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">3.8%</td>
                    <td className="px-4 py-2 text-center">24%</td>
                    <td className="px-4 py-2 text-center">72.2%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">2.8%</td>
                    <td className="px-4 py-2 text-center">18.8%</td>
                    <td className="px-4 py-2 text-center">78.4%</td>
                  </tr>
                  <tr className="border-b border-amber-100 bg-emerald-50">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center text-emerald-700">2.6%</td>
                    <td className="px-4 py-2 text-center">16.7%</td>
                    <td className="px-4 py-2 text-center">80.7%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">6.2%</td>
                    <td className="px-4 py-2 text-center">18.5%</td>
                    <td className="px-4 py-2 text-center">75.4%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">7.2%</td>
                    <td className="px-4 py-2 text-center font-bold">21.8%</td>
                    <td className="px-4 py-2 text-center font-bold">71%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              B/C roads in RED nearly tripled in two years (2.6% → 7.2%). Good-condition B/C roads
              fell from 80.7% to 71% — a loss of nearly ten percentage points in green condition since 2022.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">And This Is The Well-Funded Version</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£3.7m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£7.9m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">75%</div>
                <div className="text-brand-300 text-sm">Estimated preventative share</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Bury spends more than <span className="text-emerald-400">double its DfT allocation</span>,
              bolstered by £3.3m per year from the council's own resources plus CRSTS funding — and
              local road condition is still AMBER. The problem is not the chequebook alone.
            </p>
          </div>
        </div>
      </section>

      {/* The U-road Blind Spot */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 499.8km Residential Network
          </h2>
          <p className="text-surface-600 mb-8">
            79% of Bury's roads are unclassified — surveyed at 50% coverage each year
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED condition</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">15.1%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">28.3%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">35.2%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">21.4%</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">20.6%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The Two-Year Survey Cycle</h3>
              <p className="text-surface-600 text-sm mb-3">
                Bury's transparency report states it surveys 50% of the unclassified network annually,
                with full network coverage over two years. Unclassified roads are surveyed in only one
                direction using Coarse Visual Inspection (CVI) by accredited surveyors.
              </p>
              <p className="text-surface-600 text-sm">
                At 20.6% RED in 2024, roughly <strong>103km of residential roads</strong> were in the
                worst condition category — down from a peak of 35.2% (approximately 176km) in 2022, but
                still one road in five.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">CVI vs SCANNER</h3>
              <p className="text-amber-800 text-sm mb-3">
                A, B and C roads are assessed using SCANNER laser technology, with surveys undertaken
                in opposite directions each year so that after two years the council has data for both
                directions of travel.
              </p>
              <p className="text-amber-800 text-sm">
                U-roads rely on a visual drive-by survey covering half the network per year. The
                methodology difference matters when comparing condition across road classes — and when
                arguing what the council reasonably knew about your street.
              </p>
            </div>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="We survey 50% of the Unclassified network annually. Unclassified roads are surveyed in only one direction and we have full network coverage over two years."
              source="Bury Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, a council must show it had a reasonable system for
              knowing the condition of its roads. For Bury's unclassified network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Was your road in the 50% surveyed the year your damage occurred?</li>
              <li>• If 20.6% of U-roads were RED at the last survey, what was done about yours?</li>
              <li>• Does a CVI drive-by in one direction detect the pothole that damaged your vehicle?</li>
              <li>• How does risk-based reactive inspection interact with a road last condition-surveyed up to two years ago?</li>
            </ul>
            <p className="text-white font-medium">
              A council cannot claim detailed knowledge of 500km of residential roads when it
              condition-surveys only half of them each year — and prioritises reactive repairs by risk.
            </p>
          </div>
        </div>
      </section>

      {/* The Repair Conveyor Belt */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            54,680 Potholes in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            The scale of reactive repair tells you how many defects this network produces
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled (estimate)</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">11,451</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">10,327</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">11,738</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">10,963</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">10,201</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">54,680</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~30 Potholes a Day, Every Day</h3>
              <p className="text-surface-600 text-sm">
                Averaged over five years, Bury fills an estimated 30 potholes per day across its
                629.2km network. For 2025/26 the council plans to fill approximately 10,000 more.
                A network producing defects at that rate is one where potholes routinely form between
                inspections — exactly the scenario where prior reports and photographic evidence decide claims.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Risk-Based Reactive Repairs</h3>
              <p className="text-amber-800 text-sm">
                Pothole repairs are identified through routine highway safety inspections that adopt a
                risk-based approach, prioritising defects posing the greatest risk to road users. Not
                every pothole is treated equally — lower-risk defects may wait, which is directly
                relevant to whether the council can show it acted reasonably on yours.
              </p>
            </div>
          </div>

          <div className="space-y-6">
            <QuoteCard
              quote="Day to day reactive maintenance activities, such as pothole repairs, are identified through our system of routine highway safety inspections. These inspections adopt a risk-based approach, prioritising repairs to those defects which pose the greatest risk to road users."
              source="Bury Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>
        </div>
      </section>

      {/* Spending and investment */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            £30 Million Borrowed — Condition Still AMBER
          </h2>
          <p className="text-surface-600 mb-8">
            Bury's own account of how it funds maintenance — and what it prioritises
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">DfT allocation</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Capital spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Revenue spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Preventative</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2025/26 (proj.)</td>
                  <td className="px-4 py-3 text-center">£3,692,000</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-700">£7,926,829</td>
                  <td className="px-4 py-3 text-center">£2,590,300</td>
                  <td className="px-4 py-3 text-center">75%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center">£4,098,200</td>
                  <td className="px-4 py-3 text-center">£7,520,436</td>
                  <td className="px-4 py-3 text-center">£2,376,006</td>
                  <td className="px-4 py-3 text-center">75%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">£5,691,320</td>
                  <td className="px-4 py-3 text-center">£9,419,071</td>
                  <td className="px-4 py-3 text-center">£3,104,749</td>
                  <td className="px-4 py-3 text-center">75%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center">£3,593,000</td>
                  <td className="px-4 py-3 text-center">£6,697,600</td>
                  <td className="px-4 py-3 text-center">£3,336,763</td>
                  <td className="px-4 py-3 text-center">75%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center">£3,435,000</td>
                  <td className="px-4 py-3 text-center">£5,951,000</td>
                  <td className="px-4 py-3 text-center">£1,797,923</td>
                  <td className="px-4 py-3 text-center">75%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-mono">2020/21</td>
                  <td className="px-4 py-3 text-center">£3,555,413</td>
                  <td className="px-4 py-3 text-center">£6,245,396</td>
                  <td className="px-4 py-3 text-center">£3,977,733</td>
                  <td className="px-4 py-3 text-center">75%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Bury Council recognises the importance of optimal maintenance practices to ensure the vital highway assets that connect people, businesses and the services the council provides are serviceable and has committed to invest an additional £30 million for highway maintenance activities over and above any budget allocated by the DfT for the financial years 2017/18 to end of 2025/26."
              source="Bury Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">2024/25 Treatment Delivered</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• Surface dressed approximately 9 miles (~85,000 sq m)</li>
                <li>• Resurfaced 4.8km of A/B/C roads plus 6.5km of unclassified roads</li>
              </ul>
              <p className="text-surface-500 text-sm mt-4">
                2025/26 plans: surface dress 7.5 miles, resurface 1.25km A/B/C plus 8.5km unclassified,
                and fill approximately 10,000 potholes.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Best Practice AMBER</h3>
              <p className="text-amber-800 text-sm mb-3">
                Unlike councils with GREEN best-practice scorecards, Bury is AMBER on best practice.
                The council cites innovation trials (jet patching, thermal patching, slurry patching)
                and a geospatial asset management database — but the DfT rating suggests room for improvement
                in how effectively those practices translate to network outcomes.
              </p>
              <p className="text-amber-800 text-sm">
                From 2026/27, PAS 2161 will replace the current three-category SCANNER/CVI system with
                five categories — another methodology shift to watch when comparing year-on-year data.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What AMBER + GREEN spend means for claims */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a Well-Funded AMBER Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Bury invests heavily — here's how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — capital spend more than double DfT allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> £30m additional borrowed investment since 2017/18
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Consistent 75% preventative / 25% reactive split
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented safety inspection regime with risk-based prioritisation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> SCANNER surveys on all A/B/C roads annually (both directions over two years)
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a structured Section 58 defence on main classified roads. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — B/C RED nearly tripled from 2.6% to 7.2% since 2022
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 20.6% of U-roads still in RED — ~103km of residential network
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 79% of network surveyed at 50% coverage per year on U-roads
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 54,680 pothole fills in five years — defects outpace inspection cycles
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER best practice — DfT scorecard below top tier
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Risk-based reactive repairs mean lower-priority defects can wait
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend and £30m of borrowed investment, your claim lives or dies on
              the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council reports) — proof of actual notice</li>
              <li>• Photos showing the defect's size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road's class — on a U-road, the 50%-per-year survey gap is your strongest structural argument</li>
              <li>• Whether risk-based prioritisation left your defect untreated despite visible deterioration</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Bury's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Bury?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-funded council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road survey-gap argument</li>
                <li>• No prior-report search</li>
                <li>• No risk-prioritisation challenge</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ B/C road decline documented</li>
                <li>✅ 50%-per-year U-road survey gap argued</li>
                <li>✅ 54,680 pothole fills in five years cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Bury</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=bury"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Bury Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Bury spends more than double its DfT allocation — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN, but your claim turns on whether the specific defect
                that damaged your vehicle was reasonably inspected and repaired under Section 58 — not on
                aggregate spending. Bury's overall rating is AMBER because road condition is AMBER: B and
                C roads in RED nearly doubled from 2.6% in 2022 to 7.2% in 2024, and 20.6% of unclassified
                roads remain in RED condition.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                Unclassified roads make up 499.8km — 79% of Bury's 629.2km network. The council surveys
                only 50% of U-roads each year, achieving full network coverage over two years. At the 2024
                survey, 20.6% of U-roads were in RED condition — roughly 103km of residential streets and
                estate roads. If your incident fell in a year when your road was not surveyed, the council's
                own condition data may not cover it.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                U-road RED condition peaked at 35.2% in 2022 — does the drop to 20.6% weaken my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                No — one in five residential roads is still in RED condition at the 2024 survey. A drop from
                the 35.2% peak in 2022 does not mean your specific defect was reasonably known about or
                repaired — especially on a U-road that may only be condition-surveyed every other year under
                Bury's 50%-per-annum CVI cycle.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does Bury's risk-based inspection approach strengthen its Section 58 defence?
              </h3>
              <p className="text-surface-600 text-sm">
                It gives the council something to point to — reactive repairs are identified through "routine
                highway safety inspections" that "adopt a risk-based approach, prioritising repairs to those
                defects which pose the greatest risk to road users." But risk-based prioritisation means
                lower-priority roads and defects can wait. If your pothole was not treated as high-risk, that
                is an argument about whether the inspection regime was reasonable for your road — not an
                automatic win for either side.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Bury fills around 11,000 potholes a year — does that mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                The council filled an estimated 10,201 potholes in 2024/25 and plans to fill approximately
                10,000 in 2025/26 — roughly 28 every day. Over five years the published estimates total
                54,680 repairs on a network where condition data shows deterioration on B/C roads and one in
                five U-roads still in RED. Reactive filling is evidence of ongoing failure, not proof your
                defect was caught.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.bury.gov.uk/roads-travel-and-parking/highways-keep-bury-moving/highway-funding-and-priorities/local-highways-maintenance-transparency-report-2025-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Bury Council Local Highways Maintenance Transparency Report 2025–2026
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
